import { afterNextRender, ChangeDetectionStrategy, Component, ComponentRef, computed, DestroyRef, effect, ElementRef, inject, Injector, input, inputBinding, NgZone, outputBinding, resource, runInInjectionContext, signal, Type, untracked, ViewContainerRef, ViewEncapsulation, viewChild } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivityEntityName, BulkActionDescriptor, EntityViewDisplay, MetadataEntityData, MetadataEntityRecordScope } from '@jms/model';
import { pluralize } from 'inflected';

import { FilterFieldProvider } from '@workbuddy/ui/v2/blocks/filter';
import { PAGE_COMPONENTS } from '@workbuddy/ui/blocks/page';
import { BulkCommandArgs, EntityBoardContext, EntityBoardSettingsPanelContract, getEntityBoardListItemId, resolveBoardParams } from './context';
import { boardAsideVisible, groupBulkCommandsByGrain, resolveBulkActionErrorMessage, type BulkCommandGroup as BulkCommandGroupOf } from './model';
import { EntityBoardDensityState, EntityBoardStore } from './state';
import { BOARD_PANEL_SIZING, BOARD_PANEL_WIDE_BREAKPOINT, boardPanelCanPin, BoardPanelPreferences, boardPanelScope, BoardPanelScope, boardPanelWidthBucket, clampBoardPanelWidth, defaultBoardPanelWidth, resolveBoardPanelPinned } from './state/board.panel.preferences';
import { BoardPanelLayoutState } from './state/board.panel.layout';
import { BOARD_PANEL_DIALOG_WIDTH, type BoardPanelPresentation } from './state/board.panel.presentation';
import { BoardPanelDialogComponent } from './components/board.panel.dialog';
import { WorkBuddyDialogRef, WorkBuddyDialogService as WorkBuddyDialogs } from '@workbuddy/ui/v2/blocks/dialog';
import { BoardPanelComponent } from './components/board.panel/board.panel.component';
import { boardOpensOnDialogOutlet, boardPanelDetailsTitle, boardPanelOwnsHeader, boardPanelPreviewIsEditor } from './components/board.panel/board.panel.chrome';
import { BOARD_PANEL_NO_EXPAND, BoardPanelExpand, resolveBoardPanelExpand } from './components/board.panel/board.panel.expand';
import { resolveBoardPanelRecordPageUrl } from './components/board.panel/board.panel.record.page';
import { BoardPanelRefreshScope, BoardPanelRowPatch } from './components/board.panel/board.panel.host';
import { boardPanelRefreshPlan, boardPanelSaveAction, boardPanelUpsertsInPlace, isLiveServerSideGrid, resolveKanbanAxisChange } from './components/board.panel/board.panel.refresh';
import { EntityDialogs } from '@workbuddy/ui/v2/blocks/entity.dialog/entity.dialog.contracts';
import { EntityInspectors } from '@workbuddy/ui/v2/blocks/entity.inspector';
import { resolveBoardRowId, resolveBoardRowScopePaths } from './reorder';
import { EntityFilterFieldProvider, provideEntityBoardData } from './providers';
import { TimezoneService } from '@jms/ui/ui.standalone.services/timezone.service';
import { resolveFilterFields, resolveSecondaryMetadata, resolveUiFields } from './state/features/with.metadata';
import { BoardCardComponent } from './components/board.card';
import { BoardGanttComponent } from './components/board.gantt';
import { BoardCalendarComponent } from './components/board.calendar';
import { BoardKanbanComponent, KanbanValueSetService } from './components/board.kanban';
import { BoardHeaderComponent } from './components/board.header';
import { BoardSegmentTilesComponent } from './components/board.segment.tiles';
import { BoardListComponent } from './components/board.list';
import { BoardAttachmentsComponent } from './components/board.attachments';
import { BoardTableComponent } from './components/board.table';
import { BoardTableServerGroupsComponent } from './components/board.table.server.groups';
import { EntityOutletComponent } from '@workbuddy/ui/blocks/entity.outlet';
import { DetailPaneComponent, DetailPaneContext } from '@workbuddy/ui/v2/blocks/detail.pane';
import { BoardFocusComponent } from './components/board.focus';
import { BoardBulkActionsComponent } from './components/bulk.actions';
import { ViewSettingsComponent, ViewSettingsValue } from './components/view.settings';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { CommandPaletteContext, CommandPaletteRegistration, CommandPaletteService, useCommandPalette } from '@workbuddy/ui/v2/blocks/command.palette';
import { SettingsPanelForm } from '@workbuddy/ui/v2/blocks/settings.panel';
import { buildEntityBulkActionSettingsPanel, EntityBulkActionExecutor, resolveCommandPrimaryPanelId } from '@workbuddy/ui/v2/blocks/entity.bulk.actions';
import { Command } from '@workbuddy/ui/model';
import { EntityDataService, EntityManifestService } from '@workbuddy/services/entities';
import { ToastService, WorkBuddyDialogService } from '@workbuddy/ui/services';
import { EntityCreatorService } from '@workbuddy/modules/creators';
import { EntityBoardTopLevelDirective } from './directives';
import { ENTITY_BOARD_DIALOG_OUTLET } from './entity.board.dialog';
import { resolveBulkConfirmation } from './bulk.confirmation';
import { bulkCommandOpensPalette, BulkShortcutTarget, resolveBulkShortcutTarget } from './bulk.palette.selection';
import { resolveBoardChecklistCreateAction, resolveBoardCreateAction } from './board.create.shortcut';
import { commandKeymap, commandKeymapEntries, humanizeSemanticId, SemanticCommand, SemanticCommandId, ShortcutScope, Tier1CommandId, useShortcuts, useShortcutScope } from '@workbuddy/ui/services/keyboard.shortcuts';
import { VIEW_STATE_OWNER } from '@workbuddy/ui/v2/view.state';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { entityBoardBridgeStyles, entityBoardStyleId } from './entity.board.theme';

const bulkActionsPaletteCommandId = '__board_bulk_actions_palette__';
const bulkActionsPaletteContextId = 'entity-board.bulk-actions';
const boardShortcutScope: ShortcutScope = { kind: 'page', pageId: 'entity-board' };
// Bound with their own predicates: they act on the focused row or the panel, never on the selection.
const BOARD_OWN_SHORTCUTS: ReadonlySet<SemanticCommandId> = new Set([SemanticCommand.Create, SemanticCommand.AddFromChecklist, SemanticCommand.PreviousRecord, SemanticCommand.NextRecord]);

interface BulkCommandEntry {
  command: Command;
}

type BulkCommandGroup = BulkCommandGroupOf<BulkCommandEntry>;


@Component({
  selector: 'entity-board',
  templateUrl: './entity.board.component.html',
  styleUrls: ['./entity.board.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    PAGE_COMPONENTS,
    BoardCardComponent,
    BoardCalendarComponent,
    BoardGanttComponent,
    BoardKanbanComponent,
    BoardHeaderComponent,
    BoardSegmentTilesComponent,
    BoardListComponent,
    BoardAttachmentsComponent,
    BoardTableComponent,
    BoardTableServerGroupsComponent,
    BoardFocusComponent,
    EntityOutletComponent,
    DetailPaneComponent,
    BoardPanelComponent,
    BoardBulkActionsComponent,
    ViewSettingsComponent,
  ],
  providers: [
    EntityBoardStore,
    EntityBoardDensityState,
    BoardPanelLayoutState,
    provideEntityBoardData(),
    // Board-level so the kanban and the view editor's Columns sub-panel share one cache — opening
    // Edit View must not re-fetch the value set the board is already rendering from.
    KanbanValueSetService,
    EntityFilterFieldProvider,
    EntityCreatorService,
    WorkBuddyDialogService,
    { provide: FilterFieldProvider, useExisting: EntityFilterFieldProvider },
  ],
  host: {
    '[class.entity-board-compact]': '$compact()',
    '[class.entity-board-narrow]': '$narrow()',
    '[class.entity-board-flush]': '$flushChrome()',
    '[class.entity-board-section]': '$sectionChrome()',
    '[class.entity-board-auto-height]': '$autoHeight()',
  },
  hostDirectives: [
    {
      directive: EntityBoardTopLevelDirective,
      inputs: [
        'topLevelOverride',
      ],
    },
  ],
})
/** Core entity-board host that resolves context, provides board data, and renders collection extensions. */
export class EntityBoardComponent {
  protected readonly EntityViewDisplay = EntityViewDisplay;
  protected readonly dialogOutlet = ENTITY_BOARD_DIALOG_OUTLET;

  public $context = input.required<EntityBoardContext>({ alias: 'context' });

  protected store = inject(EntityBoardStore);
  private readonly filterProvider = inject(EntityFilterFieldProvider);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmService = inject(ConfirmServicePro);
  private readonly toast = inject(ToastService);
  private readonly paletteService = inject(CommandPaletteService);
  private readonly bulkActionExecutor = inject(EntityBulkActionExecutor);
  private readonly entityDataService = inject(EntityDataService);
  private readonly entityManifest = inject(EntityManifestService);
  private readonly panelPreferences = inject(BoardPanelPreferences);
  private readonly panelLayout = inject(BoardPanelLayoutState);
  // Root v2 dialogs; the board's own `WorkBuddyDialogService` provider is the legacy one.
  private readonly workBuddyDialogs = inject(WorkBuddyDialogs);
  // Optional: a board mounted outside the shell (a dialog, a portal) has no inspector/dialog host.
  private readonly inspectors = inject(EntityInspectors, { optional: true });
  private readonly dialogs = inject(EntityDialogs, { optional: true });
  private readonly viewStateOwner = inject(VIEW_STATE_OWNER, { optional: true });
  private readonly document = inject(DOCUMENT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  protected readonly density = inject(EntityBoardDensityState);
  private readonly bulkPaletteRegistration: CommandPaletteRegistration;
  private bulkPaletteSelectionKey: string | null = null;
  private bulkPaletteSelectedItems: readonly unknown[] | null = null;
  private bulkPaletteCommands: readonly Command[] | null = null;
  private readonly zone = inject(NgZone);
  /** Board injector for NgComponentOutlet custom display (EntityBoardStore / ENTITY_BOARD_DATA). */
  protected readonly injector = inject(Injector);
  private readonly timezone = inject(TimezoneService);

  protected readonly $settingsHost = viewChild('settingsHost', { read: ViewContainerRef });
  private readonly $boardHeader = viewChild(BoardHeaderComponent);
  protected readonly $paginatorTemplate = computed(() => this.$boardHeader()?.paginatorTemplate() ?? null);
  protected readonly $bulkActionsPaletteOpen = computed(() => this.paletteService.$openContext()?.context.id === bulkActionsPaletteContextId);
  protected readonly $statusBarHeight = computed(() => this.store.$effectiveDisplay() === EntityViewDisplay.Table ? this.store.$context()?.table?.statusBar?.height ?? 0 : 0);
  protected readonly $compact = this.density.$compact;
  protected readonly $narrow = this.density.$narrow;
  protected readonly $flushChrome = computed(() => this.store.$context()?.chrome === 'flush');
  protected readonly $sectionChrome = computed(() => this.store.$context()?.chrome === 'section' || this.store.$context()?.placement === 'section');
  protected readonly $autoHeight = computed(() => this.store.$context()?.list?.autoHeight === true);
  /** True when context/manifest declares a custom body in place of card/list/table/kanban. */
  protected readonly $useCustomDisplay = computed(() => this.store.$context()?.display?.kind === 'custom');
  /** Lazy custom body type — reactive load when display.component changes; no mount effect. */
  private readonly $customDisplayResource = resource({
    params: () => {
      const display = this.store.$context()?.display;
      return display?.kind === 'custom' ? display.component : undefined;
    },
    loader: async ({ params: load }) => (load ? load() : null),
  });
  protected readonly $customDisplayType = computed(() => this.$customDisplayResource.value() ?? null);

  protected readonly $previewContext = computed<DetailPaneContext | null>(() => {

    const target = this.store.$panelTarget();
    return target ? { ...target, readonly: this.store.$context()?.readonly === true, params: resolveBoardParams(this.store.$context()) } : null;
  });

  protected readonly $useDetailPane = computed(() => {

    const target = this.$previewContext();
    return target != null && this.entityManifest.supportsOperation(target.entity, 'preview');
  });

  /**
   * Presentation gate for the aside slot. The view editor is the other right-hand surface on this
   * row, so the aside stands down while it is open and the collection takes the width back.
   *
   * Hidden, not closed: the panel component stays mounted (its record is not re-fetched on close)
   * and no store state moves, so the aside returns exactly as it was.
   */
  protected readonly $asideVisible = computed(() => boardAsideVisible(this.store.$panelOpen(), this.store.$viewSettingsOpen()));

  // Which panel renders in the aside slot. Both are gated on $asideVisible so an explicit close —
  // or an open view editor — hides either kind; the kind itself comes from the manifest.
  protected readonly $showPreviewPanel = computed(() => this.$asideVisible() && this.store.$panelKind() === 'preview');
  protected readonly $showQuickviewPanel = computed(() => this.$asideVisible() && this.store.$panelKind() === 'quickview');
  /** The aside slot only holds the panel while it presents at the side; a dialog takes no board width. */
  protected readonly $sidePanelVisible = computed(() => this.$asideVisible() && this.store.$panelFrame() === 'side');
  // The side panel's pane, or Focus's permanent preview pane: the template holds at most one.
  private readonly $sidePanelPane = viewChild(DetailPaneComponent);
  /** `closingByBoard`: a dock or deselect, not the user dismissing the record. Per dialog, because a dismissed one finishes after its leave animation. */
  private panelDialog: { ref: WorkBuddyDialogRef<void, BoardPanelDialogComponent>; closingByBoard: boolean } | null = null;

  // Pin and width are per-device preferences, resolved from the viewport when the user has never
  // chosen. Seeded in an effect below whenever the scope key changes.
  protected readonly $panelPinned = signal(true);
  /** One px width for whichever panel is configured — both kinds size the same way. */
  protected readonly $panelWidth = signal(BOARD_PANEL_SIZING.quickview.width);
  private readonly $viewportWidth = signal(typeof window === 'undefined' ? BOARD_PANEL_WIDE_BREAKPOINT : window.innerWidth);

  // A floating panel below the pin floor is full-bleed with nothing to split against, so dragging
  // its edge would be meaningless.
  protected readonly $panelResizable = computed(() => boardPanelCanPin(this.$viewportWidth()));


  private readonly $panelScope = computed<BoardPanelScope | null>(() => {

    const entity = this.store.$contextEntity();
    const kind = this.store.$panelKind();
    if (!entity || !kind) return null;

    const surface = this.store.$contextIsEmbedded() ? 'embedded' : 'root';
    return boardPanelScope(entity, surface, boardPanelWidthBucket(this.$viewportWidth()), kind);
  });

  /**
   * The panel's dirty edits flush through the pane's leave guard first. A focus change alone only swaps
   * the pane's record, so stepping without this would drop an unblurred edit.
   */
  private async canLeavePanel(): Promise<boolean> {

    const dialog = this.panelDialog?.ref.componentRef?.instance;
    if (dialog) return dialog.canLeave();
    return await this.$sidePanelPane()?.canDeactivate() ?? true;
  }

  /** Feeds the side panel's "Uploading…" tooltips; the dialog frame reads its own pane. */
  protected readonly $sidePanelBusy = computed(() => this.$sidePanelPane()?.$busy() === true);

  /** Not a signal read: the dialog ref is plain state, and a click only needs the answer at that moment. */
  private isPanelBusy(): boolean {

    const dialog = this.panelDialog?.ref.componentRef?.instance;
    return dialog ? dialog.$busy() : this.$sidePanelBusy();
  }

  protected async onPanelStep(direction: 1 | -1): Promise<void> {

    if (!this.store.$canStep()) return;
    if (!await this.canLeavePanel()) return;
    this.store.stepFocus(direction);
  }

  /** Switching frames remounts the surface, so the same flush runs first. */
  private async setPanelPresentation(presentation: BoardPanelPresentation): Promise<void> {

    if (!await this.canLeavePanel()) return;
    this.store.setPanelPoppedOut(presentation === 'dialog');
  }

  protected async onPanelExpand(): Promise<void> {

    const kind = this.$panelExpand().kind;
    if (kind === 'dialog') return this.setPanelPresentation('dialog');
    if (!kind) return;
    // The dialog stands down while the inspector covers the board, which drops unflushed edits.
    if (kind === 'inspector' && this.panelDialog && !await this.canLeavePanel()) return;
    this.onPanelEdit();
  }

  protected onPanelDock(): void {

    void this.setPanelPresentation('side');
  }

  private syncPanelDialog(open: boolean): void {

    if (!open) {
      const current = this.panelDialog;
      if (!current) return;
      current.closingByBoard = true;
      this.panelDialog = null;
      current.ref.dismiss();
      return;
    }
    if (this.panelDialog) return;

    const ref = this.workBuddyDialogs.create<void, BoardPanelDialogComponent>(BoardPanelDialogComponent, {
      mode: 'modal',
      injector: this.injector,
      width: `min(${BOARD_PANEL_DIALOG_WIDTH}px, calc(100vw - 32px))`,
      height: 'min(85vh, 960px)',
      aria: { label: this.$panelTitle() || 'Record' },
      close: { escape: true, backdrop: true, beforeClose: () => dialog.closingByBoard || this.canLeavePanel() },
      bindings: [
        inputBinding('context', this.$previewContext),
        inputBinding('title', this.$panelTitle),
        inputBinding('ownsHeader', this.$panelOwnsHeader),
        inputBinding('expand', this.$panelExpand),
        inputBinding('recordPageUrl', this.$panelRecordPageUrl),
        outputBinding<void>('closed', () => ref.close()),
        outputBinding<1 | -1>('stepRequested', direction => void this.onPanelStep(direction)),
        outputBinding<void>('expandRequested', () => void this.onPanelExpand()),
        outputBinding<void>('dockRequested', () => this.onPanelDock()),
        outputBinding<BoardPanelRefreshScope>('refreshRequested', scope => this.onPanelRefresh(scope)),
        outputBinding<BoardPanelRowPatch>('rowPatchRequested', patch => this.store.patchDisplayedRow(patch.rowId, patch.patch)),
        outputBinding<unknown>('recordUpserted', record => this.onPanelRecordUpserted(record)),
      ],
    });
    const dialog = { ref, closingByBoard: false };
    this.panelDialog = dialog;
    void ref.result.then(() => {
      if (this.panelDialog === dialog) this.panelDialog = null;
      // Escape, backdrop and the X all mean "done with this record" — the same as closing the side panel.
      if (!dialog.closingByBoard) this.onPanelClosed();
    });
  }

  protected onPanelPinnedChange(pinned: boolean): void {

    this.$panelPinned.set(pinned);
    const scope = this.$panelScope();
    if (scope) this.panelPreferences.setPinned(scope, pinned);
  }

  protected readonly $panelTitle = computed(() => {

    const target = this.$previewContext();
    if (!target?.id) return '';
    return boardPanelDetailsTitle(target.entity);
  });

  /** False = Task Detail owns pin/float on w-page; the panel is only a resizable box. */
  protected readonly $panelOwnsHeader = computed(() => {

    const kind = this.store.$panelKind();
    if (!kind) return false;
    const target = this.$previewContext();
    const preview = target ? this.entityManifest.resolveOperation(target.entity, 'preview') : null;
    const edit = target ? this.entityManifest.resolveOperation(target.entity, 'edit') : null;
    return boardPanelOwnsHeader(kind, this.store.$panel()?.ownsHeader, boardPanelPreviewIsEditor(preview, edit));
  });

  /**
   * Capability only — authorize runs again inside dialogs/inspectors.open(), with the loaded record.
   * Financial documents open on the dialog outlet (they own their header); other editors on the inspector.
   */
  protected readonly $panelExpand = computed<BoardPanelExpand>(() => {

    const target = this.$previewContext();
    if (!target?.id) return BOARD_PANEL_NO_EXPAND;
    return resolveBoardPanelExpand({
      presentation: this.store.$panelFrameState(),
      preview: this.entityManifest.resolveOperation(target.entity, 'preview'),
      edit: this.entityManifest.resolveOperation(target.entity, 'edit'),
      canOpenInspector: !!this.inspectors,
      canOpenDialogOutlet: !!this.dialogs,
    });
  });

  protected readonly $panelRecordPageUrl = computed(() => {

    const target = this.$previewContext();
    if (!target?.id) return null;
    return resolveBoardPanelRecordPageUrl({ openPage: this.store.$panel()?.openPage, resolver: this.entityManifest.routes(target.entity)?.record, slug: this.entityManifest.slug(target.entity), edit: this.entityManifest.resolveOperation(target.entity, 'edit'), id: target.id });
  });

  private onPanelEdit(): void {

    const target = this.$previewContext();
    if (!target?.id) return;

    // Returns false when authorization denies the operation or a guard refuses; the panel simply
    // stays as it was.
    if (this.usesFinancialDocumentDialog(target.entity)) {
      void this.dialogs?.open({ operation: 'edit', entity: target.entity, id: target.id });
      return;
    }
    void this.inspectors?.open({ operation: 'edit', entity: target.entity, id: target.id, params: resolveBoardParams(this.store.$context()) });
  }

  private usesFinancialDocumentDialog(entity: ActivityEntityName): boolean {

    return boardOpensOnDialogOutlet(this.entityManifest.resolveOperation(entity, 'edit'));
  }

  /** Closing unmounts the panel, so a busy one waits through the leave guard; the dialog guards its own close. */
  protected onSidePanelClose(): void {

    this.store.whenPanelCanLeave(() => this.onPanelClosed());
  }

  protected onPanelClosed(): void {

    // Closing a selection-driven quickview means deselecting, and the sticky deselect keeps it shut
    // until the user picks another row.
    if (this.store.$panelKind() === 'quickview') {
      this.store.setFocusedItemId(null);
      return;
    }

    this.store.closePanel();
  }

  /**
   * A confirmed record from the panel, absorbed according to the board's `afterSave` policy.
   *
   * `replace` and `regroup` are the same write — the placements recompute off the new record, so a
   * changed column value moves the card on its own and every other lane is left alone. `refresh` is
   * the case the overlay cannot cover: a changed ROW value may put the record in a lane that lives
   * on another server page, and only a refetch can find out.
   */
  protected onPanelRecordUpserted(record: unknown): void {

    if (!boardPanelUpsertsInPlace(this.store.$effectiveDisplay(), record)) return;

    const key = this.store.$itemKeyOf()(record);
    const axisChange = resolveKanbanAxisChange(this.store.$effectiveView()?.kanban, this.store.$itemsByKey().get(key), record);
    const action = boardPanelSaveAction(this.store.$effectiveDisplay(), this.store.$panel()?.afterSave, axisChange);

    if (action === 'refresh') return;

    this.upsertedRecordPending = true;
    this.store.upsertItem(record);
  }

  /**
   * True while a record the panel just handed over still stands in for a page reload.
   *
   * A save emits the record and then asks for a collection refresh; the refresh is what would
   * discard the in-place update it just made, so the flag lets exactly one refresh skip the items
   * reload. Anything else — a bulk action, a delete, a command endpoint — never sets it and
   * refetches as before.
   */
  private upsertedRecordPending = false;

  /** Which refreshes a scope earns lives in `boardPanelRefreshPlan`; this only performs them. */
  protected onPanelRefresh(scope: BoardPanelRefreshScope): void {

    const target = this.$previewContext();
    const gridApi = this.store.$gridApi();
    const upsertsInPlace = this.upsertedRecordPending;
    this.upsertedRecordPending = false;
    const plan = boardPanelRefreshPlan(scope, !!target?.id, isLiveServerSideGrid(gridApi), upsertsInPlace);

    if (plan.invalidateRecord) this.entityDataService.invalidate(target.entity, [target.entity, 'item', target.id]);
    if (plan.refreshItems) this.store.refresh();
    if (plan.purgeServerSide) gridApi.refreshServerSide?.({ purge: true });
  }

  protected readonly $splitDragging = signal(false);
  private splitDragLayoutRect: DOMRect | null = null;

  protected onSplitDragStart(event: PointerEvent): void {

    const handle = event.currentTarget as HTMLElement;
    this.splitDragLayoutRect = handle.closest('.board-body-content')?.getBoundingClientRect() ?? null;
    if (!this.splitDragLayoutRect) return;

    handle.setPointerCapture(event.pointerId);
    this.$splitDragging.set(true);
    event.preventDefault();
  }

  protected onSplitDragMove(event: PointerEvent): void {

    const rect = this.splitDragLayoutRect;
    const panel = this.store.$panel();
    if (!this.$splitDragging() || !rect?.width || !panel) return;

    // The panel is anchored right, so its width is the distance from the pointer to the right edge.
    this.$panelWidth.set(clampBoardPanelWidth(panel, rect.right - event.clientX));
  }

  protected onSplitDragEnd(event: PointerEvent): void {

    if (!this.$splitDragging()) return;

    (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    this.$splitDragging.set(false);
    this.splitDragLayoutRect = null;

    // Persist on drag end only, never mid-drag.
    const scope = this.$panelScope();
    if (scope) this.panelPreferences.setWidth(scope, this.$panelWidth());
  }

  // Double-click on the handle resets to the default (standard splitter convention). The stored key
  // is removed rather than overwritten, so a future default change reaches users who reset.
  protected onSplitReset(): void {

    const panel = this.store.$panel();
    if (panel) this.$panelWidth.set(defaultBoardPanelWidth(panel));

    const scope = this.$panelScope();
    if (scope) this.panelPreferences.clearWidth(scope);
  }

  private settingsComponentRef: ComponentRef<EntityBoardSettingsPanelContract> | null = null;
  private lastMountedComponent: unknown = null;

  /**
   * Idempotent: re-runs on every context change but only resolves a period when there is none.
   *
   * Every load starts from the declared default. The period is deliberately NOT remembered across
   * loads: a stale window is invisible until the viewer notices the board is answering a question
   * they asked last week, whereas the tenant default is a stated policy about where a planner should
   * arrive. What survives is the IN-SESSION value in the store, so switching display or opening a
   * relation tab keeps the stretch of time already on screen.
   */
  private seedPeriod(): void {

    const config = this.store.$periodConfig();
    if (!this.store.$context() || !config) return;

    this.store.setPeriodTimezone(this.timezone.$timezone());
    this.store.setPeriodOffered(config.presets);
    // `defaultPreset` reads tenant settings, so it runs in the board's injector.
    const defaultPreset = () => runInInjectionContext(this.injector, () => config.defaultPreset());
    this.store.setPeriodLimit(config.notAfter ?? null, config.notAfter ? defaultPreset() : null);
    if (this.store.$periodValue()) return;

    this.store.setPeriodPreset(defaultPreset());
  }

  constructor() {

    DesignSystem.Mount(this.document, entityBoardStyleId, entityBoardBridgeStyles);
    this.store.connectContext(this.$context);

    // Seeded from an effect, never from the constructor: `$context` is a required input and is not
    // readable until Angular has bound it (NG0950 otherwise takes the whole board down). The items
    // request already sends no predicate while the range is null, and re-seeds on a context swap.
    effect(() => {
      this.store.$context();
      untracked(() => this.seedPeriod());
    });

    afterNextRender(() => {
      const element = this.host.nativeElement;
      const updateWidth = (width: number) => this.zone.run(() => {
        this.density.setWidth(width);
        this.store.setBoardWidth(width);
      });
      updateWidth(element.getBoundingClientRect().width);
      if (typeof ResizeObserver === 'undefined') return;

      const observer = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        if (width) updateWidth(width);
      });
      observer.observe(element);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });

    effect(() => this.density.setForceCompact(!!this.store.$context()?.header?.compact));

    // Width comes from the painted panel (ResizeObserver). This only clears the spacer on close so
    // a declared maxWidth is not used as the overlay inset.
    effect(() => {
      if (!this.store.$panelOpen()) this.panelLayout.setPanel(false, this.$panelPinned(), 0);
    });

    // Track the viewport so docking or undocking moves the board between width buckets, each of
    // which keeps its own pin answer. Outside the zone — only the bucket boundary matters, and the
    // signal write inside it is what triggers change detection.
    if (typeof window !== 'undefined') {
      this.zone.runOutsideAngular(() => {
        const onResize = () => {

          const next = window.innerWidth;
          const current = this.$viewportWidth();
          // Two thresholds matter: the storage bucket, and the floor below which nothing can pin.
          if (boardPanelWidthBucket(next) === boardPanelWidthBucket(current) && boardPanelCanPin(next) === boardPanelCanPin(current)) return;
          this.zone.run(() => this.$viewportWidth.set(next));
        };
        window.addEventListener('resize', onResize);
        this.destroyRef.onDestroy(() => window.removeEventListener('resize', onResize));
      });
    }

    // Seed pin and width from the per-device preference whenever the scope key changes — entity,
    // root/embedded, panel kind, or the viewport crossing the pin floor. Unset quickview
    // preference floats so opening the panel does not reflow the collection.
    effect(() => {

      const scope = this.$panelScope();
      const panel = this.store.$panel();
      if (!scope || !panel) return;

      const viewport = this.$viewportWidth();
      const canPin = boardPanelCanPin(viewport);

      untracked(() => {
        this.$panelPinned.set(resolveBoardPanelPinned(panel.kind, this.panelPreferences.pinned(scope), canPin));

        const stored = this.panelPreferences.width(scope);
        this.$panelWidth.set(stored === null ? defaultBoardPanelWidth(panel) : clampBoardPanelWidth(panel, stored));
      });
    });

    // The dialog is the same panel in another frame, so it follows the side panel's own visibility. A
    // modal would sit over an inspector opened from it, so it stands down and returns when that closes.
    effect(() => {
      const inspectorCovers = this.viewStateOwner !== 'entity-inspector' && !!this.inspectors?.$activeInspector();
      const open = this.$showQuickviewPanel() && this.store.$panelFrame() === 'dialog' && !inspectorCovers;
      untracked(() => this.syncPanelDialog(open));
    });
    this.destroyRef.onDestroy(() => this.syncPanelDialog(false));
    this.destroyRef.onDestroy(this.store.registerPanelLeaveGate({ busy: () => this.isPanelBusy(), canLeave: () => this.canLeavePanel() }));

    effect(() => {

      const entity = this.store.$contextEntity();
      const metadata = this.store.$metadata();
      const view = this.store.$view();
      const recordScope = view?.recordScope;
      if (entity && metadata) {
        const fields = resolveFilterFields(metadata, recordScope);
        const shaped = this.store.$context()?.shapeFilterFields?.(fields, view) ?? fields;
        this.filterProvider.configureFields(entity, shaped);
      }
    });

    effect(() => this.syncCustomSettings());

    effect(() => {
      const open = this.$bulkActionsPaletteOpen();
      const selectedItems = this.store.$selectedItems();
      const commands = this.store.$bulkCommands();
      const key = this.getBulkPaletteSelectionKey();
      if (!open) {
        this.bulkPaletteSelectionKey = null;
        this.bulkPaletteSelectedItems = null;
        this.bulkPaletteCommands = null;
        return;
      }
      if (this.bulkPaletteSelectionKey === null) {
        this.bulkPaletteSelectionKey = key;
        this.bulkPaletteSelectedItems = selectedItems;
        this.bulkPaletteCommands = commands;
        return;
      }
      if (this.bulkPaletteSelectionKey !== key || this.bulkPaletteSelectedItems !== selectedItems || this.bulkPaletteCommands !== commands) this.paletteService.close();
    });

    this.bulkPaletteRegistration = useCommandPalette({
      id: bulkActionsPaletteContextId,
      priority: 40,
      enabled: () => this.canRunBulkCommands(),
      context: () => this.buildBulkActionsPalette(),
    });

    this.bindBulkCommandShortcuts();
    this.bindCreateShortcut();
    this.bindChecklistCreateShortcut();
    this.bindPanelStepShortcuts();

    this.destroyRef.onDestroy(() => this.destroySettingsComponent());
  }

  /** One predicate for both the Cmd+K registration and the live chords, so they can never disagree. */
  private canRunBulkCommands(): boolean {

    return !this.store.$viewSettingsOpen() && this.store.$selectedItems().length > 0 && this.store.$bulkCommands().length > 0 && (this.viewStateOwner === 'entity-inspector' || !this.inspectors?.$activeInspector());
  }

  // Chords come only from the central keymap; the board binds each to the same target the palette
  // would dispatch for that concept.
  private bindBulkCommandShortcuts(): void {

    useShortcutScope(boardShortcutScope);
    // Create and record stepping are not bulk concepts — they act on the FOCUSED row or the panel, not
    // on the selection — so they are bound separately below. Leaving them in would register them twice.
    useShortcuts(commandKeymapEntries().filter(([semanticId]) => !BOARD_OWN_SHORTCUTS.has(semanticId)).map(([semanticId, keys]) => ({
      id: `board.${semanticId}`,
      keys,
      label: humanizeSemanticId(semanticId),
      scope: boardShortcutScope,
      when: () => this.canRunBulkCommands() && !this.paletteService.isOpen() && !!this.resolveShortcutTarget(semanticId),
      action: () => this.runShortcutTarget(semanticId),
    })));
  }

  /**
   * `N` creates in context: a child of the focused row when the mount says one is possible, otherwise
   * the board's own Add. Unlike the bulk chords it needs no selection, so it carries its own predicate
   * rather than riding `canRunBulkCommands`.
   */
  private bindCreateShortcut(): void {

    useShortcuts([{
      id: `board.${SemanticCommand.Create}`,
      keys: commandKeymap[SemanticCommand.Create],
      label: humanizeSemanticId(SemanticCommand.Create),
      scope: boardShortcutScope,
      when: () => !this.paletteService.isOpen() && !this.store.$viewSettingsOpen() && this.resolveCreateAction() != null,
      action: () => this.resolveCreateAction()?.(),
    }]);
  }

  /** Steps the open panel (or Focus's preview) through the board's records; the gate (`$canStep`) is the store's, shared with the panel's arrows. */
  private bindPanelStepShortcuts(): void {

    const steps: [Tier1CommandId, 1 | -1][] = [[SemanticCommand.PreviousRecord, -1], [SemanticCommand.NextRecord, 1]];
    useShortcuts(steps.map(([semanticId, direction]) => ({
      id: `board.${semanticId}`,
      keys: commandKeymap[semanticId],
      label: humanizeSemanticId(semanticId),
      scope: boardShortcutScope,
      when: () => !this.paletteService.isOpen() && this.store.$canStep() && (this.viewStateOwner === 'entity-inspector' || !this.inspectors?.$activeInspector()),
      action: () => void this.onPanelStep(direction),
    })));
  }

  /** Shift+N is the checklist alternative to Create and is valid only for a focused accepting row. */
  private bindChecklistCreateShortcut(): void {

    useShortcuts([{
      id: `board.${SemanticCommand.AddFromChecklist}`,
      keys: commandKeymap[SemanticCommand.AddFromChecklist],
      label: humanizeSemanticId(SemanticCommand.AddFromChecklist),
      scope: boardShortcutScope,
      when: () => !this.paletteService.isOpen() && !this.store.$viewSettingsOpen() && this.resolveChecklistCreateAction() != null,
      action: () => this.resolveChecklistCreateAction()?.(),
    }]);
  }

  /**
   * Resolved fresh on every keystroke: focus, items and the mount's own answer all move underneath.
   * The inspector guard matches the bulk predicate so a chord fired while an inspector owns the view
   * state does not run on both the inspector's board and the one behind it.
   */
  private resolveCreateAction(): (() => void) | null {

    if (this.viewStateOwner !== 'entity-inspector' && this.inspectors?.$activeInspector()) return null;

    return resolveBoardCreateAction({ context: this.store.$context(), focusedItem: this.resolveFocusedItem(), injector: this.injector });
  }

  private resolveChecklistCreateAction(): (() => void) | null {

    if (this.viewStateOwner !== 'entity-inspector' && this.inspectors?.$activeInspector()) return null;

    return resolveBoardChecklistCreateAction({ context: this.store.$context(), focusedItem: this.resolveFocusedItem(), injector: this.injector });
  }

  /**
   * The grid holds the live row node, which is the only place a projection row (an outline row whose id
   * is not its `_id`) can be read back from. `$items()` is the fallback for displays with no grid.
   */
  private resolveFocusedItem(): unknown {

    const focusedId = this.store.$focusedItemId();
    if (!focusedId) return null;

    const scopes = resolveBoardRowScopePaths(this.store.$contextRecordScopes(), this.store.$metadata());
    // Row identity first: a parent-shaped row's `_id` is its parent's, so the list-item id would match
    // the wrong sibling. The list-item id stays as the fallback for projection rows that carry `rowId`.
    return this.store.$gridApi()?.getRowNode(focusedId)?.data
      ?? this.store.$items().find(item => resolveBoardRowId(item, scopes) === focusedId)
      ?? this.store.$items().find(item => getEntityBoardListItemId(item) === focusedId)
      ?? null;
  }

  /** Resolved fresh on every keystroke — `$bulkCommands()` follows the current selection. */
  private resolveShortcutTarget(semanticId: SemanticCommandId): BulkShortcutTarget | null {

    return resolveBulkShortcutTarget(semanticId, this.store.$bulkCommands());
  }

  private runShortcutTarget(semanticId: SemanticCommandId): void {

    const target = this.resolveShortcutTarget(semanticId);
    if (!target) return;
    // executeBulkCommand owns its own confirmation prompt, so a destructive chord still asks first.
    if (target.kind === 'bulk-action') {
      void this.executeBulkCommand(target.command);
      return;
    }
    // Deep-link straight into the command's value panel — landing on the palette root would only
    // highlight the row and leave the user to click through to the picker the chord already named.
    const initialPanelId = resolveCommandPrimaryPanelId(target.command) ?? undefined;
    void this.bulkPaletteRegistration.open({ initialPanelId, initialPanelTitle: initialPanelId ? target.command.label : undefined });
  }

  public refresh(): void {

    this.store.refresh();
  }

  // Whether the view/settings editor is currently open. Consumers can mirror
  // this to gate sibling actions (e.g. disabling a create button while editing).
  public readonly $settingsOpen = this.store.$viewSettingsOpen;

  public openSettings(mode: 'edit' | 'create' = 'edit'): void {

    this.store.setViewSettingsOpen(true, mode);
  }

  public async requestCloseSettings(): Promise<boolean> {

    if (!this.store.$viewSettingsOpen()) return true;

    const settingsInstance = this.settingsComponentRef?.instance;
    if (settingsInstance?.requestClose) return await settingsInstance.requestClose();

    this.store.setViewSettingsOpen(false);
    return true;
  }

  private syncCustomSettings(): void {

    const host = this.$settingsHost();
    const component = this.store.$context()?.settings?.component;
    const open = this.store.$viewSettingsOpen();

    if (!host || !component || !open) {
      this.destroySettingsComponent();
      return;
    }

    if (this.lastMountedComponent === component) return;

    this.destroySettingsComponent();
    this.mountSettingsComponent(host, component);
    this.lastMountedComponent = component;
  }

  private mountSettingsComponent(host: ViewContainerRef, component: Type<EntityBoardSettingsPanelContract>): void {

    this.settingsComponentRef = host.createComponent(component, {
      bindings: [
        inputBinding('isOpen', this.store.$viewSettingsOpen),
        inputBinding('mode', () => 'push'),
        inputBinding('data', computed(() => this.store.$context()?.settings?.data)),
        outputBinding<ViewSettingsValue>('previewViewChange', value => this.onPreviewLayoutChange(value)),
        outputBinding<ViewSettingsValue>('previewRequestChange', value => this.onPreviewRequestChange(value)),
        outputBinding<unknown>('saved', value => this.onCustomSettingsSaved(value)),
        outputBinding<unknown>('saveAsNew', value => this.onCustomSettingsSaveAsNew(value)),
        outputBinding<unknown>('reset', () => this.onCustomSettingsReset()),
        outputBinding<unknown>('deleted', () => this.onCustomSettingsDeleted()),
        outputBinding<unknown>('cancelled', () => this.onCustomSettingsCancelled()),
        outputBinding<unknown>('closed', () => this.onCustomSettingsClosed()),
      ],
    });

  }

  private destroySettingsComponent(): void {

    this.settingsComponentRef?.destroy();
    this.settingsComponentRef = null;
    this.lastMountedComponent = null;
  }

  private async onCustomSettingsSaved(value: unknown): Promise<void> {

    const settings = this.store.$context()?.settings;
    if (settings?.onSave) {
      await settings.onSave((value ?? this.currentSettingsValue()) as ViewSettingsValue);
      this.store.setViewSettingsOpen(false);
    } else {
      this.store.saveView();
    }
  }

  private async onCustomSettingsSaveAsNew(value: unknown): Promise<void> {

    const settings = this.store.$context()?.settings;
    if (settings?.onSaveAsNew) {
      await settings.onSaveAsNew((value ?? this.currentSettingsValue()) as ViewSettingsValue);
      this.store.setViewSettingsOpen(false);
    } else {
      this.store.saveViewAsNew();
    }
  }

  private onCustomSettingsReset(): void {

    const settings = this.store.$context()?.settings;
    if (settings?.onReset) {
      settings.onReset();
    } else {
      this.store.resetView();
    }
  }

  private onCustomSettingsDeleted(): void {

    const settings = this.store.$context()?.settings;
    if (settings?.onDelete) {
      settings.onDelete();
    } else {
      this.onViewDeleted();
    }
  }

  private onCustomSettingsCancelled(): void {

    this.store.setViewSettingsOpen(false);
    this.store.$context()?.settings?.onCancel?.();
  }

  private onCustomSettingsClosed(): void {

    this.store.setViewSettingsOpen(false);
    this.store.$context()?.settings?.onClose?.();
  }

  private currentSettingsValue(): ViewSettingsValue {

    return {
      view: this.store.$previewView() ?? {},
      pageSize: this.store.$effectivePageSize(),
      request: this.store.$previewRequest() ?? undefined,
    };
  }

  protected onViewSettingsChange(value: ViewSettingsValue): void {

    const prevGroupBy = normalizeGroupBy((this.store.$effectiveView()?.table as { groupBy?: string | null })?.groupBy);
    const nextGroupBy = normalizeGroupBy((value.view?.table as { groupBy?: string | null })?.groupBy);
    const pageSizeChanged = value.pageSize !== this.store.$effectivePageSize();
    const groupByChanged = nextGroupBy !== prevGroupBy;

    this.store.setPreviewView({ ...value.view, pageSize: value.pageSize }, value.request);

    // Mirror display-settings: groupBy / pageSize change result set or paging grain — reset + reload
    if (pageSizeChanged || groupByChanged) {
      this.store.clearSelectedItems();
      this.store.updateHeader({ page: 0, pageSize: value.pageSize });
      queueMicrotask(() => this.store.refresh());
    }
  }

  protected onPreviewLayoutChange(value: ViewSettingsValue): void {

    const prevGroupBy = normalizeGroupBy((this.store.$effectiveView()?.table as { groupBy?: string | null })?.groupBy);
    const nextGroupBy = normalizeGroupBy((value.view?.table as { groupBy?: string | null })?.groupBy);
    const pageSizeChanged = value.pageSize !== this.store.$effectivePageSize();
    const groupByChanged = nextGroupBy !== prevGroupBy;

    this.store.setPreviewLayout({ ...value.view, pageSize: value.pageSize });

    if (pageSizeChanged || groupByChanged) {
      this.store.clearSelectedItems();
      this.store.updateHeader({ page: 0, pageSize: value.pageSize });
      queueMicrotask(() => this.store.refresh());
    }
  }

  protected onPreviewRequestChange(value: ViewSettingsValue): void {

    this.store.setPreviewRequest(value.request ?? null);
  }

  protected onViewSettingsSaved(): void {

    if (this.store.$viewSettingsMode() === 'create') {
      this.store.saveViewAsNew();
      return;
    }
    this.store.saveView();
  }

  protected onViewSettingsSaveAsNew(): void {

    this.store.saveViewAsNew();
  }

  protected async onViewDeleted(): Promise<void> {

    const view = this.store.$view();
    if (!view?._id) return;

    const confirmed = await this.confirmService.confirm('Delete View', `Are you sure you want to delete "${view.name}"?`, 'danger', 'Delete', 'Cancel');
    if (confirmed) this.store.deleteView();
  }

  protected onBulkCommand(action: { action: string }): void {

    if (this.store.$ganttWritable?.() === false) {
      return;
    }

    if (action.action === bulkActionsPaletteCommandId) {
      void this.openBulkActionsPalette();
      return;
    }

    const command = this.store.$bulkCommands().find(commandItem => commandItem._id === action.action);
    if (!command) {
      return;
    }

    if (bulkCommandOpensPalette(command)) {
      const initialPanelId = resolveCommandPrimaryPanelId(command) ?? undefined;
      void this.bulkPaletteRegistration.open({ initialPanelId, initialPanelTitle: command.label });
      return;
    }

    void this.executeBulkCommand(command);
  }

  private async executeBulkCommand(command: Command): Promise<void> {

    if (this.store.$ganttWritable?.() === false) {
      return;
    }

    const args = this.store.triggerBulkCommand(command);
    if (!args) return;
    const entity = args.bulkActionRequest?.entity ?? this.store.$contextEntity();
    if (!entity) return;

    const confirmation = resolveBulkConfirmation(this.resolveBulkActionDescriptor(command) ?? undefined, args.data.length, this.store.$metadata()?.displayName);
    await this.bulkActionExecutor.execute({
      context: 'board',
      entity,
      args,
      boardContext: this.store.$context(),
      injector: this.injector,
      destroyRef: this.destroyRef,
      handleCommand: current => this.store.$context()?.bulk?.onCommand?.(current),
      confirm: () => confirmation ? this.confirmService.confirm(confirmation.title, confirmation.body, 'danger') : true,
      onStarted: () => this.store.clearSelectedItems(),
      onCompleted: () => {
        // 'collection' scope also invalidates the focused record's item query — the details panel
        // reads [entity, 'item', id], which a plain refresh() (items + counts only) never touches.
        this.onPanelRefresh('collection');
        this.invokeBulkActionComplete();
      },
    });
  }

  private invokeBulkActionComplete(): void {

    const context = this.store.$context();
    if (!context?.bulk?.onComplete) return;

    try {
      context.bulk.onComplete(context);
    } catch (error) {
      this.toast.error('Bulk action follow-up failed', resolveBulkActionErrorMessage(error));
    }
  }

  private async openBulkActionsPalette(): Promise<void> {

    await this.bulkPaletteRegistration.open();
  }

  private buildBulkActionsPalette(): CommandPaletteContext | null {

    if (this.store.$ganttWritable?.() === false) {
      return null;
    }

    const commands = this.store.$bulkCommands();
    const selectedItems = this.store.$selectedItems();
    if (!commands.length || !selectedItems.length) return null;
    this.bulkPaletteSelectionKey = this.getBulkPaletteSelectionKey();
    this.bulkPaletteSelectedItems = selectedItems;
    this.bulkPaletteCommands = commands;

    return {
      id: bulkActionsPaletteContextId,
      scopeChip: { label: this.store.$context()?.bulk?.describeSelection?.(selectedItems) || `${selectedItems.length} selected` },
      settingsPanel: this.getBulkActionsSettingsPanel(selectedItems),
    };
  }

  private getBulkPaletteSelectionKey(): string {

    return `${this.store.$selectedItems().length}:${this.store.$bulkCommands().map(command => command._id).join(',')}`;
  }

  private getBulkActionsSettingsPanel(selectedItems: unknown[]): SettingsPanelForm {

    // Command-palette mode is single-shot: every open builds a fresh form so no
    // stale picker values leak across sessions.
    return this.buildBulkActionsSettingsPanel(selectedItems.length);
  }

  private buildBulkActionsSettingsPanel(selectedCount: number): SettingsPanelForm {

    const commandConfigs: BulkCommandEntry[] = this.store.$bulkCommands().map(command => ({ command }));
    const groups = this.groupBulkCommands(commandConfigs);
    return buildEntityBulkActionSettingsPanel({
      title: `${selectedCount} selected`,
      groups: groups.map(group => ({ label: group.label, commands: group.entries.map(entry => entry.command) })),
      execute: command => {
        void this.executeBulkCommand(command);
        this.paletteService.close();
      },
    });
  }

  private groupBulkCommands(commandConfigs: BulkCommandEntry[]): BulkCommandGroup[] {

    const sections = this.store.$context()?.bulk?.groupCommands?.(commandConfigs.map(entry => entry.command));
    if (sections) return sections.map(section => ({ label: section.label, entries: section.commands.map(command => ({ command })) }));

    const metadata = this.store.$metadata();
    if (metadata?.type === ActivityEntityName.EntityQueue) return this.groupQueueBulkCommands(metadata, commandConfigs);

    const primaryLabel = metadata?.displayName ? pluralize(metadata.displayName) : null;
    const secondaryMetadata = resolveSecondaryMetadata(metadata);
    const secondaryLabel = secondaryMetadata?.displayName ? pluralize(secondaryMetadata.displayName) : null;

    return groupBulkCommandsByGrain(commandConfigs, {
      descriptorOf: entry => this.resolveBulkActionDescriptor(entry.command),
      grainOf: descriptor => this.resolveBoardGrainLabel(descriptor, primaryLabel, secondaryLabel),
    });
  }

  private resolveBoardGrainLabel(descriptor: BulkActionDescriptor, primaryLabel: string | null, secondaryLabel: string | null): string | null {

    if (descriptor.scope === MetadataEntityRecordScope.Secondary) return secondaryLabel;
    if (descriptor.scope === MetadataEntityRecordScope.Primary || descriptor.scope === MetadataEntityRecordScope.Any) return primaryLabel;

    // Deeper path grains (e.g. workOrders.codes) have no display name to hand — the path keeps them apart.
    return typeof descriptor.scope === 'string' ? descriptor.scope : null;
  }

  private groupQueueBulkCommands(metadata: MetadataEntityData, commandConfigs: BulkCommandEntry[]): BulkCommandGroup[] {

    // Queue grains have no inherent order, so sort by label to keep the sections stable across renders.
    // groupBulkCommandsByGrain then emits them in the order encountered.
    const byGrain = [...commandConfigs].sort((left, right) => {
      const leftLabel = this.resolveQueueGrainLabel(metadata, this.resolveBulkActionDescriptor(left.command)) ?? '';
      const rightLabel = this.resolveQueueGrainLabel(metadata, this.resolveBulkActionDescriptor(right.command)) ?? '';
      return leftLabel.localeCompare(rightLabel);
    });

    return groupBulkCommandsByGrain(byGrain, {
      descriptorOf: entry => this.resolveBulkActionDescriptor(entry.command),
      grainOf: descriptor => this.resolveQueueGrainLabel(metadata, descriptor),
    });
  }

  private resolveQueueGrainLabel(metadata: MetadataEntityData, descriptor: BulkActionDescriptor | null | undefined): string | null {

    if (!descriptor) return null;
    if (descriptor.id === 'close') return 'Flag';

    const fieldId = this.normalizeQueueDescriptorField(descriptor.field);

    // Commands carry no field to group by, so the grain stamp is the only signal.
    if (!fieldId) return descriptor.scope === MetadataEntityRecordScope.Secondary ? 'Stage' : this.resolveQueuePrimaryGroupLabel(metadata);

    const field = metadata.fields?.find(currentField => this.queueFieldMatchesDescriptor(currentField.path ?? currentField.name, fieldId));
    if (field?.group) return field.group;

    if (fieldId.startsWith('flag.')) return 'Flag';
    if (fieldId.startsWith('entity.workOrders.')) return 'Stage';
    if (fieldId.startsWith('entity.') || fieldId.startsWith('customFields.')) return this.resolveQueuePrimaryGroupLabel(metadata);

    return null;
  }

  private resolveQueuePrimaryGroupLabel(metadata: MetadataEntityData): string {

    return metadata.fields?.find(field => (field.path ?? field.name)?.startsWith('entity.') && !(field.path ?? field.name)?.startsWith('entity.workOrders.'))?.group ?? 'Entity';
  }

  private normalizeQueueDescriptorField(fieldId: string | undefined): string | undefined {

    return fieldId?.replace(/^customFields\.(entity|flag)\./, '$1.');
  }

  private queueFieldMatchesDescriptor(metadataFieldId: string | undefined, descriptorFieldId: string): boolean {

    if (!metadataFieldId) return false;
    if (metadataFieldId === descriptorFieldId) return true;
    return metadataFieldId === descriptorFieldId.replace(/^customFields\.(entity|flag)\./, '$1.');
  }

  private resolveBulkActionDescriptor(command: Command): BulkActionDescriptor | null {

    return (command.data as { bulkActionDescriptor?: BulkActionDescriptor } | undefined)?.bulkActionDescriptor ?? null;
  }

}

function normalizeGroupBy(value: string | null | undefined): string | null {

  if (value == null || value === '' || value === 'none') return null;
  return value;
}
